import * as caTables from "./tables.js";
import * as caPrompts from "./prompts.js";
import * as arTables from "./ar-tables.js";

export const DEFAULT_PROJECT_STATE = "CA";

const CA_PACK = Object.freeze({
  state: "CA", label: "California (CA)", active: true,
  tables: caTables, prompts: caPrompts,
  evidenceStates: Object.freeze(["CA"]),
  passes: Object.freeze({ cascade: true, flow2: true, associations: true, partsRescue: true }),
  usesAlignmentOpCodes: true,  // [WPAR4 2026-08-12] VEH §3065's "related to" clause is what makes an alignment op code evidence
});

// WP-AR1 contract stub: test/loadable, but deliberately not active or runnable until WP-AR2
// supplies Arkansas tables/prompts and flips `active`. The upload picker reads active packs only.
const AR_PACK = Object.freeze({
  state: "AR", label: "Arkansas (AR)", active: true, stub: false,
  tables: arTables, prompts: null,  // [WPAR2-R5 2026-08-12] prompt selection is by state at the call sites today; consuming builders from the pack is the cleanup-batch consolidation
  evidenceStates: Object.freeze(["AR"]),
  passes: Object.freeze({ cascade: false, flow2: false, associations: false, partsRescue: false }),
  // [WPAR4 2026-08-12] Blake's ruling: Ark. Code Ann. §23-112-313 has NO "related to" clause, so the
  // alignment feature does not exist under AR law. classifyAr already never reads op codes; this field
  // is what lets the UI stop asking for them (registry is the single authority on what a pack does).
  usesAlignmentOpCodes: false,
});

export const LAW_PACKS = Object.freeze({ CA: CA_PACK, AR: AR_PACK });
export const activeLawPacks = () => Object.values(LAW_PACKS).filter(pack => pack.active);

export function getLawPack(state) {
  const requestedState = String(state || DEFAULT_PROJECT_STATE).trim().toUpperCase() || DEFAULT_PROJECT_STATE;
  const pack = LAW_PACKS[requestedState];
  if (pack) return { ...pack, requestedState, fallback: false, warning: null };
  return {
    ...CA_PACK, requestedState, fallback: true,
    warning: `Unknown project state “${requestedState}”. California law was loaded as the safe fallback. Upload a fresh project with a supported state.`,
  };
}

export function projectLawSelection(state, { trainee = false } = {}) {
  return getLawPack(trainee ? DEFAULT_PROJECT_STATE : state);
}

export const entryState = entry => String(entry?.state || DEFAULT_PROJECT_STATE).trim().toUpperCase();
export const filterEntriesForState = (entries, state) => {
  const selected = getLawPack(state).state;
  return (Array.isArray(entries) ? entries : []).filter(entry => entryState(entry) === selected);
};
